import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineryService } from '../../../services/machineryService';
import Toast from '../../../components/Common/Toast';

const statusLabels = {
  active: 'En operación',
  maintenance: 'En mantenimiento',
  inactive: 'Inactivo',
  retired: 'Retirado'
};

const ApartadoMaquina = ({ machine, showStatus = true }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-teal-200 transition-all overflow-hidden">
    <div className="p-4 flex flex-col h-full">
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-amber-100 text-amber-700 text-lg" title="Máquina">🔧</span>
        {showStatus && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${
            machine.status === 'maintenance' ? 'bg-amber-100 text-amber-800' : machine.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
          }`}>
            {statusLabels[machine.status] || machine.status}
          </span>
        )}
      </div>
      <p className="font-semibold text-gray-900 text-sm">{machine.code}</p>
      <p className="text-gray-600 text-sm mt-0.5 line-clamp-2">{machine.name}</p>
      {machine.location && <p className="text-gray-500 text-xs mt-1 truncate">{machine.location}</p>}
      <Link
        to={`/machinery/events/lista?machine_id=${machine.id}`}
        className="mt-4 inline-flex items-center justify-center w-full py-2 px-3 rounded-lg bg-teal-50 text-teal-700 text-sm font-medium hover:bg-teal-100 transition-colors"
      >
        Ver bitacora →
      </Link>
    </div>
  </div>
);

const ApartadoVehiculo = ({ vehicle, showStatus = true }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-teal-200 transition-all overflow-hidden">
    <div className="p-4 flex flex-col h-full">
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-100 text-blue-700 text-lg" title="Vehículo">🚗</span>
        {showStatus && (
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${
            vehicle.status === 'maintenance' ? 'bg-amber-100 text-amber-800' : vehicle.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'
          }`}>
            {statusLabels[vehicle.status] || vehicle.status}
          </span>
        )}
      </div>
      <p className="font-semibold text-gray-900 text-sm">{vehicle.code}</p>
      <p className="text-gray-600 text-sm mt-0.5 line-clamp-2">{vehicle.name}</p>
      {vehicle.license_plate && <p className="text-gray-500 text-xs mt-1">Placa: {vehicle.license_plate}</p>}
      <Link
        to={`/machinery/events/lista?vehicle_id=${vehicle.id}`}
        className="mt-4 inline-flex items-center justify-center w-full py-2 px-3 rounded-lg bg-teal-50 text-teal-700 text-sm font-medium hover:bg-teal-100 transition-colors"
      >
        Ver bitacora →
      </Link>
    </div>
  </div>
);

const EventsOverview = () => {
  const [machines, setMachines] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [assetsRes, categoriesRes] = await Promise.all([
        machineryService.getMachineryFormData(),
        machineryService.getCategories({ active_only: true }),
      ]);

      if (assetsRes.success && assetsRes.data) {
        setMachines(assetsRes.data.machines || []);
        setVehicles(assetsRes.data.vehicles || []);
      } else {
        setToast({ isOpen: true, message: assetsRes.message || 'Error al cargar datos', type: 'error' });
      }

      if (categoriesRes.success && Array.isArray(categoriesRes.data)) {
        setCategories(categoriesRes.data);
      } else {
        setCategories([]);
        setToast({ isOpen: true, message: categoriesRes.message || 'Error al cargar líneas', type: 'error' });
      }
    } catch (error) {
      setToast({ isOpen: true, message: 'Error al cargar datos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
          <p className="mt-2 text-gray-600">Cargando maquinaria y vehículos...</p>
        </div>
      </div>
    );
  }

  const machinesEnOperacion = machines.filter((m) => m.status !== 'maintenance');
  const machinesEnMantenimiento = machines.filter((m) => m.status === 'maintenance');
  const vehiclesEnOperacion = vehicles.filter((v) => v.status !== 'maintenance' && v.status !== 'retired');
  const vehiclesEnMantenimiento = vehicles.filter((v) => v.status === 'maintenance');
  const vehiclesRetirados = vehicles.filter((v) => v.status === 'retired');

  const categoriesById = categories.reduce((acc, cat) => {
    acc[String(cat.id)] = cat;
    return acc;
  }, {});

  const getLineLabel = (asset, expectedScope) => {
    const categoryId = asset?.category_id;
    if (!categoryId) return 'Otro';
    const category = categoriesById[String(categoryId)];
    if (!category || category.scope !== expectedScope) return 'Otro';
    return category.name || 'Otro';
  };

  const groupByLine = (items, scope) =>
    items.reduce((acc, item) => {
      const line = getLineLabel(item, scope);
      if (!acc[line]) acc[line] = [];
      acc[line].push(item);
      return acc;
    }, {});

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Bitacora</h1>
        <p className="text-gray-600 mt-1">Seleccione un vehículo o una máquina para ver su bitacora (eventos con horómetro y kilometraje)</p>
      </div>

      {/* Operaciones: vehículos y máquinas en operación */}
      <section className="mb-10">
        <h2 className="text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <span className="text-green-600">●</span> Operaciones
        </h2>
        <p className="text-sm text-gray-500 mb-4">Vehículos y maquinarias en operación. Desde aquí puede ver la bitácora de cada unidad.</p>

        <div className="mb-6">
          <h3 className="text-base font-medium text-gray-700 mb-3">Vehículos ({vehiclesEnOperacion.length})</h3>
          {vehiclesEnOperacion.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200 border-dashed p-6 text-center">
              <p className="text-gray-500 text-sm">No hay vehículos en operación</p>
            </div>
          ) : (
            Object.entries(groupByLine(vehiclesEnOperacion, 'vehicle')).map(([lineName, lineVehicles]) => (
              <div key={`veh-op-${lineName}`} className="mb-6 last:mb-0">
                <h4 className="text-sm font-semibold text-teal-700 mb-3">Línea: {lineName} ({lineVehicles.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {lineVehicles.map((v) => (
                    <ApartadoVehiculo key={v.id} vehicle={v} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">Máquinas ({machinesEnOperacion.length})</h3>
          {machinesEnOperacion.length === 0 ? (
            <div className="bg-gray-50 rounded-xl border border-gray-200 border-dashed p-6 text-center">
              <p className="text-gray-500 text-sm">No hay máquinas en operación</p>
            </div>
          ) : (
            Object.entries(groupByLine(machinesEnOperacion, 'machine')).map(([lineName, lineMachines]) => (
              <div key={`maq-op-${lineName}`} className="mb-6 last:mb-0">
                <h4 className="text-sm font-semibold text-amber-700 mb-3">Línea: {lineName} ({lineMachines.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {lineMachines.map((m) => (
                    <ApartadoMaquina key={m.id} machine={m} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Mantenimiento: vehículos y máquinas en mantenimiento */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <span className="text-amber-600">●</span> Mantenimiento
        </h2>
        <p className="text-sm text-gray-500 mb-4">Unidades enviadas a mantenimiento. Desde Mantenimientos puede dar la orden de mandar a mantenimiento y aquí aparecerán con estado &quot;En mantenimiento&quot;.</p>

        <div className="mb-6">
          <h3 className="text-base font-medium text-gray-700 mb-3">Vehículos ({vehiclesEnMantenimiento.length})</h3>
          {vehiclesEnMantenimiento.length === 0 ? (
            <div className="bg-amber-50/50 rounded-xl border border-amber-200 border-dashed p-6 text-center">
              <p className="text-gray-500 text-sm">No hay vehículos en mantenimiento</p>
            </div>
          ) : (
            Object.entries(groupByLine(vehiclesEnMantenimiento, 'vehicle')).map(([lineName, lineVehicles]) => (
              <div key={`veh-man-${lineName}`} className="mb-6 last:mb-0">
                <h4 className="text-sm font-semibold text-teal-700 mb-3">Línea: {lineName} ({lineVehicles.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {lineVehicles.map((v) => (
                    <ApartadoVehiculo key={v.id} vehicle={v} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">Máquinas ({machinesEnMantenimiento.length})</h3>
          {machinesEnMantenimiento.length === 0 ? (
            <div className="bg-amber-50/50 rounded-xl border border-amber-200 border-dashed p-6 text-center">
              <p className="text-gray-500 text-sm">No hay máquinas en mantenimiento</p>
            </div>
          ) : (
            Object.entries(groupByLine(machinesEnMantenimiento, 'machine')).map(([lineName, lineMachines]) => (
              <div key={`maq-man-${lineName}`} className="mb-6 last:mb-0">
                <h4 className="text-sm font-semibold text-amber-700 mb-3">Línea: {lineName} ({lineMachines.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {lineMachines.map((m) => (
                    <ApartadoMaquina key={m.id} machine={m} />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Vehículos retirados */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <span className="text-gray-500">●</span> Vehículos retirados
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Unidades dadas de baja. Puede consultar su bitácora histórica.
        </p>

        {vehiclesRetirados.length === 0 ? (
          <div className="bg-gray-50 rounded-xl border border-gray-200 border-dashed p-6 text-center">
            <p className="text-gray-500 text-sm">No hay vehículos retirados</p>
          </div>
        ) : (
          Object.entries(groupByLine(vehiclesRetirados, 'vehicle')).map(([lineName, lineVehicles]) => (
            <div key={`veh-ret-${lineName}`} className="mb-6 last:mb-0">
              <h4 className="text-sm font-semibold text-gray-600 mb-3">Línea: {lineName} ({lineVehicles.length})</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {lineVehicles.map((v) => (
                  <ApartadoVehiculo key={v.id} vehicle={v} />
                ))}
              </div>
            </div>
          ))
        )}
      </section>

      <Toast isOpen={toast.isOpen} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, isOpen: false })} />
    </div>
  );
};

export default EventsOverview;
